import { Box, Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import 'moment/locale/en-gb';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { MetadataError } from '@ep/error-handling';
import { ReplicationRow, ReplicationRowValue } from '@esg/esg-global-types';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import ExportDataButton, {
  ExportRequestParameters,
  ExportParametersProps
} from '../ExportDataButton';
import ReplicationRowSelect from '../../shared/input/select/ReplicationRowSelect';
import { site_label } from '../../../lib/metric_capture/site';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import { getReplicationRows } from '../../../graphql/services/replication.service';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import BasicSelect from '../../shared/input/select/BasicSelect';
import { generateMonthRangeStrings } from '../../../util/date';

const SOURCE = 'ReportingMonthlyDataUtilityManagementExportParameters.tsx';

type UtilityType = 'electricity' | 'water';

type ReportingType = 'consumption' | 'calculated_cost' | 'bill_variance' | 'time_of_use';

// Replication entities to use for export filters.
interface ReportingMonthlyUtilityManagementReplicationEntities {
  sites: Array<ReplicationRow>;
  meters: Array<ReplicationRow>;
}

// Data sent in request to unique Cloud Function.
interface ReportingMonthlyDataUtilityManagementExportRequestParameters extends ExportRequestParameters {
  group_id: string;
  company_id: string;
  source_client_dataset: string;
  reporting_months: Array<string>;
  site_ids: Array<string>;
  utility_type: UtilityType | null;
  reporting_type: ReportingType | null;
}

/**
 * Reusable export context parameters for reporting monthly data from a company's Utility Management programme.
 * @param {void} handleContextChange Update context in parent from input changes.
 * @param {void} handleContextValid Update context validity in parent based on input changes.
 * @returns {JSX.Element}
 */
const ReportingMonthlyDataUtilityManagementExportParameters = ({
  export_type
}: ExportParametersProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!user || !group || !company) {
    return null;
  }

  const utility_types: Array<UtilityType> = ['electricity', 'water'];

  const reporting_types: Array<ReportingType> = [
    'consumption',
    'calculated_cost',
    'bill_variance',
    'time_of_use'
  ];

  // Default parameters state for inputs.
  const initial_parameters: ReportingMonthlyDataUtilityManagementExportRequestParameters = {
    group_id: group.id,
    company_id: company.id,
    source_client_dataset: company.client_bigquery_dataset,
    reporting_months: [],
    site_ids: [],
    utility_type: null,
    reporting_type: null
  };

  const initial_entities_map: ReportingMonthlyUtilityManagementReplicationEntities = {
    sites: [],
    meters: []
  };

  const [reportingMonthlyExportParameters, setReportingMonthlyExportParameters] =
    useState<ReportingMonthlyDataUtilityManagementExportRequestParameters>(initial_parameters);
  const [replicationEntities, setReplicationEntities] =
    useState<ReportingMonthlyUtilityManagementReplicationEntities>(initial_entities_map);
  const [selectedSites, setSelectedSites] = useState<Array<ReplicationRow>>([]);
  const [selectedCategories, setSelectedCategories] = useState<Array<string>>([]);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  const default_min_date: Moment = moment('2009-01-01').startOf('day');

  // Validation check for unique export parameters.
  const parameters_valid = Boolean(
    startDate &&
    endDate &&
    reportingMonthlyExportParameters.reporting_months.length &&
    reportingMonthlyExportParameters.site_ids.length &&
    reportingMonthlyExportParameters.utility_type &&
    reportingMonthlyExportParameters.reporting_type
  );

  const site_categories: Set<string> = new Set(
    replicationEntities.sites.reduce(
      (current_categories: Array<string>, next_site: ReplicationRow) => {
        return [
          ...current_categories,
          ...(next_site.data.category_1 && typeof next_site.data.category_1 === 'string'
            ? [next_site.data.category_1]
            : [])
        ];
      },
      []
    )
  );

  const handleStartMonthChange = (start_month: Moment | null): void => {
    const updated_parameters: ReportingMonthlyDataUtilityManagementExportRequestParameters = {
      ...reportingMonthlyExportParameters,
      reporting_months: generateMonthRangeStrings(start_month, endDate)
    };
    setStartDate(start_month ? start_month.startOf('month') : null);
    setReportingMonthlyExportParameters(updated_parameters);
  };

  const handleEndMonthChange = (end_month: Moment | null): void => {
    const updated_parameters: ReportingMonthlyDataUtilityManagementExportRequestParameters = {
      ...reportingMonthlyExportParameters,
      reporting_months: generateMonthRangeStrings(startDate, end_month)
    };
    setEndDate(end_month ? end_month.startOf('month') : null);
    setReportingMonthlyExportParameters(updated_parameters);
  };

  const handleUtilityTypeChange = (utility_type: UtilityType | null): void => {
    const updated_parameters: ReportingMonthlyDataUtilityManagementExportRequestParameters = {
      ...reportingMonthlyExportParameters,
      utility_type: utility_type,
      reporting_type: null,
      site_ids: []
    };
    setSelectedCategories([]);
    setSelectedSites([]);
    setReportingMonthlyExportParameters(updated_parameters);
  };

  const handleReportingTypeChange = (reporting_type: ReportingType | null): void => {
    const updated_parameters: ReportingMonthlyDataUtilityManagementExportRequestParameters = {
      ...reportingMonthlyExportParameters,
      reporting_type: reporting_type,
      site_ids: []
    };
    setSelectedCategories([]);
    setSelectedSites([]);
    setReportingMonthlyExportParameters(updated_parameters);
  };

  const handleCategoriesChange = (categories: Array<string>): void => {
    const updated_parameters: ReportingMonthlyDataUtilityManagementExportRequestParameters = {
      ...reportingMonthlyExportParameters,
      site_ids: [],
      meter_ids: []
    };
    setSelectedCategories(categories);
    setSelectedSites([]);
    setReportingMonthlyExportParameters(updated_parameters);
  };

  const handleSitesChange = (sites: Array<ReplicationRow>): void => {
    const updated_parameters: ReportingMonthlyDataUtilityManagementExportRequestParameters = {
      ...reportingMonthlyExportParameters,
      site_ids: sites.map((site: ReplicationRow) => site.id),
      meter_ids: []
    };
    setSelectedSites(sites);
    setReportingMonthlyExportParameters(updated_parameters);
  };

  const getFilteredSites = (): Array<ReplicationRow> => {
    return replicationEntities.sites.filter((site: ReplicationRow) => {
      const site_category: ReplicationRowValue = site.data.category_1;
      return (
        selectedCategories.length === 0 ||
        (!!site_category &&
          typeof site_category === 'string' &&
          selectedCategories.includes(site_category))
      );
    });
  };

  // Display reporting type options based on selected utility type.
  const getFilteredReportingTypes = (): Array<ReportingType> => {
    return reporting_types.filter((reporting_type: ReportingType) => {
      const selected_utility: UtilityType | null = reportingMonthlyExportParameters.utility_type;
      return (
        selected_utility === 'electricity' ||
        (selected_utility === 'water' && reporting_type !== 'time_of_use')
      );
    });
  };

  // Fetch replication entities needed for export parameter filtering.
  const fetchReplicationEntities = async () => {
    try {
      const [sites, meters]: [Array<ReplicationRow>, Array<ReplicationRow>] = await Promise.all([
        getReplicationRows(
          {
            group_id: group.id,
            company_id: company.id,
            replication_id: 'bigquery_utility_management',
            table_id: 's_configuration_sites'
          },
          {}
        ),
        getReplicationRows(
          {
            group_id: group.id,
            company_id: company.id,
            replication_id: 'bigquery_utility_management',
            table_id: 's_configuration_meter_list'
          },
          {}
        )
      ]);
      if (sites && meters) {
        setReplicationEntities({ sites, meters });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
          {
            group: group,
            company: company,
            user: user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch meters and sites', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  useEffect(() => {
    (async () => {
      setReportingMonthlyExportParameters(initial_parameters);
      setReplicationEntities(initial_entities_map);
      if (group && company && user) {
        await fetchReplicationEntities();
      }
    })();
  }, [user, company, group]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Grid container spacing={2}>
        <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
          <Box width={350}>
            <DatePicker<Moment>
              views={['year', 'month']}
              value={startDate}
              label={'Start Month'}
              maxDate={endDate || moment()}
              minDate={default_min_date}
              onChange={(new_date: Moment | null) => handleStartMonthChange(new_date)}
              sx={{ width: '100%' }}
            />
          </Box>
          <Box width={350}>
            <DatePicker<Moment>
              views={['year', 'month']}
              value={endDate}
              label={'End Month'}
              maxDate={moment()}
              minDate={startDate || default_min_date}
              onChange={(new_date: Moment | null) => handleEndMonthChange(new_date)}
              sx={{ width: '100%' }}
            />
          </Box>
        </Grid>
        <Grid size={12}>
          <Box width={350}>
            <BasicSelect
              input_label="Utility"
              options={utility_types}
              selected_options={reportingMonthlyExportParameters.utility_type}
              option_label_title_case
              handleChangeSelection={(utility_type: string | Array<string> | null): void => {
                if (!Array.isArray(utility_type)) {
                  handleUtilityTypeChange(utility_type as UtilityType | null);
                }
              }}
            />
          </Box>
        </Grid>
        {!!reportingMonthlyExportParameters.utility_type && (
          <Grid size={12}>
            <Box width={350}>
              <BasicSelect
                input_label="Reporting Type"
                options={getFilteredReportingTypes()}
                selected_options={reportingMonthlyExportParameters.reporting_type}
                option_label_title_case
                handleChangeSelection={(reporting_type: string | Array<string> | null): void => {
                  if (!Array.isArray(reporting_type)) {
                    handleReportingTypeChange(reporting_type as ReportingType | null);
                  }
                }}
              />
            </Box>
          </Grid>
        )}
        {!!reportingMonthlyExportParameters.utility_type &&
          !!reportingMonthlyExportParameters.reporting_type && (
            <>
              <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
                <Box width={350}>
                  <BasicSelect
                    input_label="Category"
                    options={[...site_categories]}
                    multi_select
                    disable_selected_tags
                    select_all
                    selected_options={selectedCategories}
                    handleChangeSelection={(categories: string | Array<string> | null): void => {
                      if (Array.isArray(categories)) {
                        handleCategoriesChange(categories);
                      }
                    }}
                  />
                </Box>
                <Box width={350}>
                  <ReplicationRowSelect
                    disable_selected_tags
                    multi_select
                    options={getFilteredSites()}
                    selected_options={selectedSites}
                    replication_entity_label={site_label}
                    option_label_field="site_name"
                    select_all
                    handleChangeReplicationRows={(
                      rows: Array<ReplicationRow> | ReplicationRow | null
                    ) => {
                      if (Array.isArray(rows)) {
                        handleSitesChange(rows);
                      }
                    }}
                  />
                </Box>
              </Grid>
            </>
          )}
        <Grid size={12} sx={{ textAlign: 'left' }}>
          <ExportDataButton
            user={user}
            export_type={export_type}
            parameters={reportingMonthlyExportParameters}
            disabled={!parameters_valid}
            label="Export"
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default ReportingMonthlyDataUtilityManagementExportParameters;
